import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import RouteSelectionPanel from './components/RouteSelectionPanel';
import GeometryUploadModal from './components/GeometryUploadModal';
import InteractiveMapEditor from './components/InteractiveMapEditor';
import { routeService } from '../../services/routeService';

const RouteGeometryManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [currentGeometry, setCurrentGeometry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('routes');

  useEffect(() => {
    loadRoutes();
  }, []);

  const loadRoutes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await routeService?.getAll();
      
      const formattedRoutes = data?.map(route => ({
        id: route?.id,
        route_name: route?.routeName,
        start_point: route?.startPoint,
        end_point: route?.endPoint,
        active: route?.isActive,
        has_geometry: route?.geometry ? true : false,
        distance_meters: route?.distanceKm ? route?.distanceKm * 1000 : 0,
        geometry: route?.geometry
      }));
      
      setRoutes(formattedRoutes);
    } catch (err) {
      console.error('Error loading routes:', err);
      setError(err?.message || 'Rotalar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRoute = (route) => {
    setSelectedRoute(route);
    setCurrentGeometry(route?.geometry);
    setActiveTab('map');
  };

  const handleGeometryChange = (newGeometry) => {
    setCurrentGeometry(newGeometry);
  };

  const handleSaveGeometry = async () => {
    if (!selectedRoute || !currentGeometry) return;

    setIsSaving(true);
    setError(null);

    try {
      const distance = calculateTotalDistance(currentGeometry?.coordinates);
      const distanceKm = distance / 1000;

      await routeService?.update(selectedRoute?.id, {
        routeName: selectedRoute?.route_name,
        startPoint: selectedRoute?.start_point,
        endPoint: selectedRoute?.end_point,
        isActive: selectedRoute?.active,
        geometry: currentGeometry,
        distanceKm: distanceKm,
        durationMinutes: selectedRoute?.duration_minutes,
        departureTimes: selectedRoute?.departure_times || []
      });

      setRoutes(prevRoutes =>
        prevRoutes?.map(route =>
          route?.id === selectedRoute?.id
            ? {
                ...route,
                has_geometry: true,
                distance_meters: distance,
                geometry: currentGeometry
              }
            : route
        )
      );

      setSelectedRoute(prev => ({
        ...prev,
        has_geometry: true,
        distance_meters: distance,
        geometry: currentGeometry
      }));
    } catch (err) {
      console.error('Error saving geometry:', err);
      setError(err?.message || 'Geometri kaydedilirken hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadGeometry = async (uploadData) => {
    setError(null);
    
    try {
      const route = routes?.find(r => r?.id === uploadData?.routeId);
      if (!route) throw new Error('Rota bulunamadı');

      const distanceKm = uploadData?.distance_meters / 1000;

      await routeService?.update(uploadData?.routeId, {
        routeName: route?.route_name,
        startPoint: route?.start_point,
        endPoint: route?.end_point,
        isActive: route?.active,
        geometry: uploadData?.geometry,
        distanceKm: distanceKm,
        durationMinutes: route?.duration_minutes,
        departureTimes: route?.departure_times || []
      });

      setRoutes(prevRoutes =>
        prevRoutes?.map(r =>
          r?.id === uploadData?.routeId
            ? {
                ...r,
                has_geometry: true,
                distance_meters: uploadData?.distance_meters,
                geometry: uploadData?.geometry
              }
            : r
        )
      );

      const updatedRoute = routes?.find(r => r?.id === uploadData?.routeId);
      if (updatedRoute) {
        setSelectedRoute({
          ...updatedRoute,
          has_geometry: true,
          distance_meters: uploadData?.distance_meters,
          geometry: uploadData?.geometry
        });
        setCurrentGeometry(uploadData?.geometry);
      }

      setIsUploadModalOpen(false);
    } catch (err) {
      console.error('Error uploading geometry:', err);
      setError(err?.message || 'Geometri yüklenirken hata oluştu');
      throw err;
    }
  };

  const calculateTotalDistance = (coordinates) => {
    if (!coordinates || coordinates?.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 0; i < coordinates?.length - 1; i++) {
      const [lng1, lat1] = coordinates?.[i];
      const [lng2, lat2] = coordinates?.[i + 1];
      totalDistance += getDistanceBetweenPoints(lat1, lng1, lat2, lng2);
    }
    return Math.round(totalDistance);
  };

  const getDistanceBetweenPoints = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <AdminSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        userName="Admin"
        userRole="Sistem Yöneticisi"
      />

      <main
        className={`flex-1 flex flex-col transition-smooth overflow-hidden ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 md:px-6 border-b border-border bg-card">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground font-heading">
              Geometri Yönetimi
            </h1>
            <p className="text-xs md:text-sm text-muted-foreground">
              Rota çizgilerini yükleyin ve düzenleyin
            </p>
          </div>
        </div>

        {error && (
          <div className="mx-4 md:mx-6 mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-destructive hover:text-destructive/80 transition-smooth"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="lg:hidden flex border-b border-border bg-card">
          <button
            onClick={() => setActiveTab('routes')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
              activeTab === 'routes' ?'text-primary border-b-2 border-primary' :'text-muted-foreground'
            }`}
          >
            Rotalar
          </button>
          <button
            onClick={() => setActiveTab('map')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
              activeTab === 'map' ?'text-primary border-b-2 border-primary' :'text-muted-foreground'
            }`}
          >
            Harita
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div
            className={`w-full lg:w-80 xl:w-96 flex-shrink-0 ${
              activeTab === 'routes' ? 'block' : 'hidden lg:block'
            }`}
          >
            <RouteSelectionPanel
              routes={routes}
              selectedRoute={selectedRoute}
              onSelectRoute={handleSelectRoute}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onNewGeometry={() => setIsUploadModalOpen(true)}
            />
          </div>

          <div
            className={`flex-1 p-4 md:p-6 overflow-auto ${
              activeTab === 'map' ? 'block' : 'hidden lg:block'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-sm text-muted-foreground">Yükleniyor...</p>
                </div>
              </div>
            ) : (
              <InteractiveMapEditor
                selectedRoute={selectedRoute}
                geometry={currentGeometry}
                onGeometryChange={handleGeometryChange}
                onSave={handleSaveGeometry}
                isSaving={isSaving}
              />
            )}
          </div>
        </div>
      </main>

      <GeometryUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadGeometry}
        routes={routes}
      />
    </div>
  );
};

export default RouteGeometryManagement;